/*
Available extended methods (super may be required!)
- refresh                       - runs when drupal is refreshed or can be run internally when required

Available methods
- onReady                       - runs when (min-width: 0rem) is hit
- onMobileNavInit               - runs when (max-width: 46.875rem) is hit
- onMobileNavDestroy            - runs when (max-width: 46.875rem) is hit

Available methods when Config.pluginswindowListenerPlugin.scroll == true
- scroll                        - runs every time scroll events are fired
- debouncedScroll               - runs every configured Xms to delay scroll events and improve performance
- delayedDebouncedScroll        - runs every time scroll events end firing after configured Xms

Available methods when Config.pluginswindowListenerPlugin.resize == true
- resize                        - runs every time resize events are fired
- debouncedResize               - runs every configured Xms to delay resize events and improve performance
- delayedDebouncedResize        - runs every time resize events end firing after configured Xms
*/

// imports
import MainApp from 'minsky-kit/js/managers/MainApp';
import InstanceManager from 'minsky-kit/js/managers/InstanceManager';

import Config from './Config'; // keep even though we don't use it
import WADMenuOverlay from './minsky/components/WADMenuOverlay';
import VideoHero from './minsky/components/VideoHero';
import WADAccordionGroup from './minsky/components/WADAccordionGroup';
import Overview from './minsky/components/Overview';
import MinskyHighway from './minsky/managers/MinskyHighway';
import InviewManager from './minsky/components/InviewManager';
import WordFlipper from './minsky/components/WordFlipper';
import ContactForm from './minsky/components/ContactForm';
import { runFrontAnimation } from './minsky/animations/frontAnimation';
import { initContactAnimation } from './minsky/animations/contactAnimation';

window['MINSKY_DEV'] = window['MINSKY_DEV'] || {};
window['MINSKY_DEV'].allImageSizesOnLoad = false;

// run animation immediately
runFrontAnimation();

// class
export default class ProjectApp extends MainApp {
    // export default class ProjectApp extends MinskyApp {
    // constructor
    constructor(args = {}) {
        // set app instance debug state
        args.debug = false;

        // call super constructor
        super(args, 'Project App');

        // properties
        this._im = new InstanceManager(); // https://bitbucket.org/Minsky_WebAgency/minsky-kit/src/master/documentation/InstanceManager.md
        this._isHighwayRefresh = false;
        this._isMobile = null;

        if (!this.loggedIn) {
            // add highway readyness
            document.querySelector('body .page').classList.add('page--has-highway');

            this._highway = new MinskyHighway({
                navigationLinks: Array.from(document.body.querySelectorAll('.menu__link')),
            });

            // add listeners
            this._highway.on('beforeDrupalRefresh', this.blm.add('beforeDrupalRefresh', onBeforeDrupalRefresh));

            window.hw = this._highway;
        }
        else {
            document.querySelector('body .page').classList.add('page--no-highway');
        }
    }

    // methods
    onReady() {
        this.log('App ready');

        // video heroes
        this._im.initAddComponent('video-heroes', VideoHero, '.node__video-hero:not(.video-hero--ready)');

        // accordion groups
        this._im.initAddComponent('accordion-groups', WADAccordionGroup, '.accordion-group:not(.accordion-group--ready)');

        // work overview
        this._im.initAddComponent('overviews', Overview, [
            '.node__view--work-overview .views-element-container',
            '.node__view--work-teasers .views-element-container',
            '.node__view--article-overview .views-element-container:not(.overview--ready)',
        ].join(', '));

        // add listeners
        this._im.get('overviews').forEach((overview) => {
            if (!overview.$el.classList.contains('overview--highway-ready')) {
                overview.on(['view-rows_refresh', 'view_refresh'], this.blm.addOnce('onOveviewRefresh', onOveviewRefresh));
                overview.$el.classList.add('overview--highway-ready');
            }
        });

        // inviews
        this._im.destroy('inviews');
        this._im.initAddComponent('inviews', InviewManager, [
            '.paragraph:not(.guide-cta-p):not(.list-p) .paragraph__content',
            '.guide-cta__content',
            '.list-p__text',
            '.node-view-intro',
            '.views-row',
            'contact__contact',
            'contact__newsletter'
        ].join(','));

        // word flippers
        this._im.initAddComponent('word-flippers', WordFlipper, '.word-flipper');
    }

    refresh() {
        this.log('App refresh');

        if (this._isHighwayRefresh) {
            // use media query manager to fake an initial init
            // make sure --ready modifiers are checked to avoid double inits
            this.mqManager.resetMatches();
            this.mqManager.check();
            this._isHighwayRefresh = false;
        }
        else {
            this._im.run([
                'accordion-groups',
                'word-flipper',
            ], 'refresh');

            super.refresh();
        }

        if (!this._isMobile) initContactAnimation();
    }

    resize() {
        this.log('App resize');
    }

    debouncedResize() {
        this.log('App debounced resize');
    }

    delayedDebouncedResize() {
        this._im.run([
            'accordion-groups',
            'overviews',
            'menu',
            'word-flipper',
        ], 'refresh');
    }

    scroll() {
        this.log('App scroll');
    }

    onMobileNavInit() {
        this.log('Mobile');
        this._isMobile = true;

        // hamburger & menu overlay
        const $hamburger = document.querySelector('.hamburger:not(.hamburger--ready)');
        const $navigation = document.querySelector('.header__navigation');
        const $overlayParent = document.querySelector('.page__inner');
        if ($hamburger && $navigation) {
            this._im.add('menu', new WADMenuOverlay({
                $source: $navigation,
                $hamburger,
                $parent: $overlayParent,
                $header: $overlayParent ? $overlayParent.querySelector('.page__header') : null,
            }));
        }
    }

    onMobileNavDestroy() {
        this.log('Desktop');
        this._isMobile = false;

        // destroy instances
        this._im.destroy([
            'menu',
        ], 'destroy');

        initContactAnimation();

        // contact form
        // this._im.initAddComponent('contact-wipe', ContactForm, '.contact__contact .contact-form-block');
    }
}

// utilities

// event handlers
function onBeforeDrupalRefresh(e) {
    this._isHighwayRefresh = true;
}

function onOveviewRefresh(e) {
    // get all item links && quickly detach known links and reatach them all
    const $links = e.currentTarget.$el.querySelectorAll('.view-rows a');
    this._highway.detach($links);
    this._highway.attach($links);
}
